import { Box, Button, Stack, Tab, Tabs, Typography } from "@mui/material";
import { ts } from "src/utils/translate";
import { useGetFloorsQuery, useGetProductsOfRoomsByFloorsQuery } from "src/api/v2/floor-v2-ApiSlice";
import { useNavigate, useParams } from "react-router";
import { TwoColumnPageBase } from "src/components/PageBase/TwoColumnPageBase";
import React, { SyntheticEvent, useEffect, useRef, useState } from "react";
import { FloorView } from "src/pages/PlannerPage/FloorView/FloorView";
import { ProjectMenu } from "src/components/ProjectMenu/ProjectMenu";
import { useGetProjectQuery } from "src/api/v2/project-v2-ApiSlice";
import { TabPanel } from "src/components/TabPanel/TabPanel";
import { useGetDevicesQuery } from 'src/api/v2/device-v2-ApiSlice';
import { DeviceView } from 'src/pages/PlannerPage/DeviceView/DeviceView';
import { FloorWizard } from "src/pages/PlannerPage/FloorWizard/FloorWizard";
import AppHeader from 'src/components/AppHeader/AppHeader';
import {
    GetDevicesOfProjectResponse,
    GetFloorsResponse,
    GetProductsOfRoomsByFloorsResponse,
    GetProjectResponseProject,
    GetRoomTypesResponse
} from "src/model/api/v2/data-contracts";
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { plannerUiSlice, PlannerUiState } from 'src/api/planner-ui-slice';
import { Planner } from 'src/components/Planner/Planner';
import { useGetRoomTypesQuery } from 'src/api/v2/room-type-v2-ApiSlice';
import { RoomRecognitionFlow } from 'src/pages/PlannerPage/RoomRecognition/RoomRecognitionFlow';
import { RecognitionContext } from 'src/pages/PlannerPage/RoomRecognition/recognitionTypes';
import { FirstWalkthrough } from 'src/pages/PlannerPage/Walkthrough/FirstWalkthrough';

export type CustomItemDrop = { x: number, y: number, roomId: number, rot: number };
export type NoteDrop = { x: number, y: number, floorId: number };
export type NoteEdit = { id: number, x: number, y: number, text: string, color: string };
export type NoteEditOnDevice = {
    id: number,
    deviceId: string | number,
    text: string,
    color: string,
};
export type NoteAddToDevice = { deviceId: string | number, floorId: number };

interface PlannerPageState {
    project: GetProjectResponseProject | undefined,
    getFloorsResponse: GetFloorsResponse | undefined,
    productsOfRoomsByFloors: GetProductsOfRoomsByFloorsResponse | undefined,
    getProducts: GetDevicesOfProjectResponse | undefined,
    isProjectLoading: boolean,
    isFloorsLoading: boolean,
    isProductsOfRoomLoading: boolean
    isProductsLoading: boolean,
    getRoomTypesResponse: GetRoomTypesResponse | undefined,
    plannerUiState: PlannerUiState
}

function usePlannerPageState(): PlannerPageState {
    const params = useParams<{ projectId: string }>();

    const {data: roomTypes} = useGetRoomTypesQuery({ isTranslated: true, excludeUnassigned: true})
    const {data: project, isLoading: isProjectLoading} = useGetProjectQuery(Number(params.projectId))
    const {data: floors, isLoading: isFloorsLoading} = useGetFloorsQuery({ project_id: Number(params.projectId) });
    const {
        data: productsOfRoomsByFloors,
        isLoading: isProductsOfRoomLoading
    } = useGetProductsOfRoomsByFloorsQuery(Number(params.projectId));
    const {data: getProducts, isLoading: isProductsLoading} = useGetDevicesQuery(Number(params.projectId));
    const plannerUiState = useAppSelector((state) => state.plannerUi);

    return {
        project: project,
        getFloorsResponse: floors,
        productsOfRoomsByFloors: productsOfRoomsByFloors,
        getProducts: getProducts,
        isProjectLoading: isProjectLoading,
        isFloorsLoading: isFloorsLoading,
        isProductsOfRoomLoading: isProductsOfRoomLoading,
        isProductsLoading: isProductsLoading,
        getRoomTypesResponse: roomTypes,
        plannerUiState: plannerUiState
    }
}

export const PlannerPage = () => {
    const state = usePlannerPageState();
    const [tabIndex, setTabIndex] = useState(0);

    const projectId = Number(useParams<{ projectId: string }>().projectId);
    const navigate = useNavigate();

    const [walkthroughFloorId, setWalkthroughFloorId] = useState<number | null>(null);
    const walkthroughActive = walkthroughFloorId != null;
    const [offerBtnEl, setOfferBtnEl] = useState<HTMLElement | null>(null);

    const [customItemDrop, setCustomItemDrop] = useState<CustomItemDrop | null>(null);
    const [noteDrop, setNoteDrop] = useState<NoteDrop | null>(null);
    const [noteEdit, setNoteEdit] = useState<NoteEdit | null>(null);
    const [noteEditOnDevice, setNoteEditOnDevice] = useState<NoteEditOnDevice | null>(null);
    const [noteAddToDevice, setNoteAddToDevice] = useState<NoteAddToDevice | null>(null);
    const [isFloorWizardOpen, setIsFloorWizardOpen] = useState(false);
    const [recognitionContext, setRecognitionContext] = useState<RecognitionContext | null>(null);

    const dispatch = useAppDispatch();

    const handleTabChange = (_: SyntheticEvent, newTabIndex: number) =>
        setTabIndex(newTabIndex);

    const handleOfferClick = () => {
        if (walkthroughActive) {
            dispatch(plannerUiSlice.actions.setFirstWalkthroughOfferPending(true));
        }
        navigate(`/offer/${projectId}`);
    };

    const handleRecognitionUploadNewLayout = () => {
        setRecognitionContext(null);
        setIsFloorWizardOpen(true);
    };

    const didAutoSelectFloorRef = useRef(false);
    useEffect(() => {
        if (didAutoSelectFloorRef.current) return;
        const firstFloorId = state.getFloorsResponse?.floors[0]?.id;
        if (firstFloorId == null) return;
        if (state.plannerUiState.selectedFloorId == null) {
            dispatch(plannerUiSlice.actions.setSelectedFloorId(firstFloorId));
        }
        didAutoSelectFloorRef.current = true;
    }, [state.getFloorsResponse, state.plannerUiState.selectedFloorId, dispatch]);

    useEffect(() => {
        if (state.plannerUiState.openFloorWizardAfterProjectCreation) {
            setIsFloorWizardOpen(true);
            dispatch(plannerUiSlice.actions.setOpenFloorWizardAfterProjectCreation(false));
        }
    }, [state.plannerUiState.openFloorWizardAfterProjectCreation, dispatch]);


    if (!state.project) {
        return (
            <AppHeader/>
        );
    }

    let selectedFloor = null;
    if (state.getFloorsResponse != null && state.plannerUiState.selectedFloorId != null) {
      selectedFloor = state.getFloorsResponse.floors.find(
          (floor) => floor.id === state.plannerUiState.selectedFloorId);
    }

    return (
        <>
            <TwoColumnPageBase
                fullscreen={state.plannerUiState.isFullscreen}
                leftContent={
                    <Stack direction="column" spacing={1} sx={{alignItems: "center"}}>
                        <Stack
                            direction="row"
                            sx={{
                                justifyContent: "space-between",
                                alignItems: "center",
                                width: "100%"
                            }}
                        >
                            <Typography variant={"h6"}>
                                {state.project.project.project_name}
                            </Typography>
                            <ProjectMenu
                                extendedProject={state.project}
                                showOpenProject={false}
                                iconButtonSx={{ p: 0 }}
                            />
                        </Stack>
                        <Tabs
                            value={tabIndex}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            sx={{width: "100%"}}
                        >
                            <Tab label={ts("planner_devices", "Devices")}/>
                            <Tab label={ts("planner_floors", "Floors")}/>
                        </Tabs>
                        <TabPanel value={tabIndex} index={0}>
                            {state.getProducts ? (
                                <DeviceView
                                    getDevices={state.getProducts}
                                    customItemDrop={customItemDrop}
                                    setCustomItemDrop={setCustomItemDrop}
                                    noteDrop={noteDrop}
                                    setNoteDrop={setNoteDrop}
                                    noteEdit={noteEdit}
                                    setNoteEdit={setNoteEdit}
                                    noteEditOnDevice={noteEditOnDevice}
                                    setNoteEditOnDevice={setNoteEditOnDevice}
                                    noteAddToDevice={noteAddToDevice}
                                    setNoteAddToDevice={setNoteAddToDevice}
                                />
                            ) : (
                                <Typography color="text.secondary">
                                    {ts("planner_empty_devices", "No devices yet.")}
                                </Typography>
                            )}
                        </TabPanel>
                        <TabPanel value={tabIndex} index={1}>
                            {state.productsOfRoomsByFloors ? (
                                <FloorView
                                    projectId={projectId}
                                    productsOfRoomsByFloors={state.productsOfRoomsByFloors}
                                    onAddFloor={() => setIsFloorWizardOpen(true)}
                                />
                            ) : (
                                <Typography color="text.secondary">
                                    {ts("planner_empty_floors", "No floors yet.")}
                                </Typography>
                            )}
                        </TabPanel>
                    </Stack>
                }
                pageContent={
                    <Box sx={{ position: "relative", width: "100%" }}>
                        <Button
                            ref={setOfferBtnEl}
                            variant="contained"
                            onClick={handleOfferClick}
                            sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}
                        >
                            {ts("projectListMenuOffer", "Offer")}
                        </Button>
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{justifyContent: "space-between", alignItems: "start"}}
                        >
                            {selectedFloor != null && state.getRoomTypesResponse != null && (
                                <Planner
                                    key={`${selectedFloor.id}-${selectedFloor.plan_image}`}
                                    floorId={selectedFloor!!.id!!}
                                    userRoleInProject={state.project.userRole}
                                    floorImageUrl={selectedFloor.plan_image!!}
                                    roomTypes={state.getRoomTypesResponse!!.roomTypes}
                                    onCustomItemDrop={setCustomItemDrop}
                                    onNoteDrop={setNoteDrop}
                                    onNoteEdit={setNoteEdit}
                                    onNoteEditOnDevice={setNoteEditOnDevice}
                                    onNoteAddToDevice={setNoteAddToDevice}
                                />
                            )}
                        </Stack>
                    </Box>
                }
            />

            {isFloorWizardOpen && (
                <FloorWizard
                    projectId={projectId}
                    onClose={() => setIsFloorWizardOpen(false)}
                    onStartRecognition={setRecognitionContext}
                />
            )}

            {recognitionContext != null && (
                <RoomRecognitionFlow
                    key={recognitionContext.floorId}
                    context={recognitionContext}
                    onFinished={() => setRecognitionContext(null)}
                    onShowFloorList={() => setTabIndex(1)}
                    onUploadNewLayout={handleRecognitionUploadNewLayout}
                    onStartAutoPlan={() => setWalkthroughFloorId(recognitionContext.floorId)}
                />
            )}

            {walkthroughFloorId != null && (
                <FirstWalkthrough
                    offerButtonEl={offerBtnEl}
                    onFinished={() => setWalkthroughFloorId(null)}
                />
            )}

        </>
    );
};
